<template>
  <span v-if="config" class="payment-status">
    <v-tooltip location="top" :disabled="!details">
      <template #activator="{ props }">
        <v-chip
          v-bind="props"
          :color="config.color"
          :variant="config.variant"
          :prepend-icon="config.icon"
          size="x-small"
          label
          class="payment-chip"
        >
          {{ config.label }}
        </v-chip>
      </template>
      <span>{{ details }}</span>
    </v-tooltip>
    <v-tooltip v-if="matchConfig" location="top" :disabled="!changesText">
      <template #activator="{ props }">
        <v-chip
          v-bind="props"
          :color="matchConfig.color"
          :variant="matchConfig.variant"
          :prepend-icon="matchConfig.icon"
          size="x-small"
          label
          class="payment-chip"
        >
          {{ matchConfig.label }}
        </v-chip>
      </template>
      <div class="changes-tooltip">
        <div class="font-weight-bold">{{ matchConfig.title }}</div>
        {{ changesText }}
      </div>
    </v-tooltip>
    <v-chip
      v-if="quote.paymentIsTest && quote.paymentStatus !== 'pending'"
      color="orange"
      variant="outlined"
      size="x-small"
      label
      class="payment-chip"
      title="Paid through Foxy's test gateway: no real money moved"
    >
      TEST
    </v-chip>
  </span>
</template>

<script setup>
import { computed } from "vue";
import { formatCheckoutChange } from "../utils";

// Online payment state of a quote (Foxy), from the quotes API:
// paymentStatus, paidAt, amountPaid, foxyDisplayId, paymentIsTest, and
// checkoutMatch / checkoutChanges (was the cart changed before paying?).
const props = defineProps({
  quote: { type: Object, required: true },
});

const STATUSES = {
  // The stored status stays "paid": only the label reads as the checkout's end.
  paid: { label: "Checkout completed", color: "success", variant: "flat", icon: "mdi-check-circle" },
  pending: { label: "Checkout started", color: "grey", variant: "tonal", icon: "mdi-cart-arrow-right" },
  refunded: { label: "Refunded", color: "warning", variant: "outlined", icon: "mdi-cash-refund" },
  voided: { label: "Voided", color: "grey", variant: "outlined", icon: "mdi-cancel" },
};

const config = computed(() => STATUSES[props.quote.paymentStatus] || null);

const MATCHES = {
  changed: { label: "Changed", title: "Changed at checkout", color: "warning", variant: "flat", icon: "mdi-cart-remove" },
  unknown: { label: "Not compared", title: "Not compared", color: "grey", variant: "outlined", icon: "mdi-help-circle-outline" },
};

// Only once paid: a pending checkout has nothing to compare yet.
const matchConfig = computed(() =>
  props.quote.paymentStatus && props.quote.paymentStatus !== "pending"
    ? MATCHES[props.quote.checkoutMatch] || null
    : null
);

const changesText = computed(() => {
  const changes = props.quote.checkoutChanges;
  if (props.quote.checkoutMatch === "unknown") {
    return "Saved before the calculator sent its cart, so there was nothing to compare against";
  }
  return Array.isArray(changes) ? changes.map(formatCheckoutChange).join("\n") : "";
});

const details = computed(() => {
  const { amountPaid, paidAt, foxyDisplayId } = props.quote;
  const parts = [];
  if (amountPaid !== null && amountPaid !== undefined) {
    parts.push(`$${Number(amountPaid).toFixed(2)}`);
  }
  if (paidAt) {
    const d = new Date(paidAt);
    parts.push(`${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`);
  }
  if (foxyDisplayId) {
    parts.push(`Foxy #${foxyDisplayId}`);
  }
  return parts.join(" · ");
});
</script>

<style scoped>
.payment-status {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 3px;
  align-items: center;
}

.payment-chip {
  padding: 0 6px;
  font-size: 0.6875rem;
  letter-spacing: 0;
}

.payment-chip :deep(.v-chip__prepend .v-icon) {
  font-size: 12px;
  margin-inline-end: 3px;
}

.changes-tooltip {
  white-space: pre-line;
  max-width: 320px;
}
</style>
